/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'
import { useIntl } from 'react-intl'

import { EuiFormRow, EuiSuperSelect } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import CloudProvider from '@modules/cloud-components/CloudProvider'
import type { CSP } from '@modules/ui-types/providers'

type Props = {
  value: CSP
  onChange: (value: CSP) => void
  isDisabled: boolean
}

const OPTIONS: Array<{
  value: CSP
  inputDisplay: JSX.Element
  dropdownDisplay: JSX.Element
}> = [
  {
    value: 'aws',
    inputDisplay: <CloudProvider id='aws' text='short' showIcon={true} />,
    dropdownDisplay: <CloudProvider id='aws' text='long' showIcon={true} />,
  },
  {
    value: 'gcp',
    inputDisplay: <CloudProvider id='gcp' text='short' showIcon={true} />,
    dropdownDisplay: <CloudProvider id='gcp' text='long' showIcon={true} />,
  },
  {
    value: 'azure',
    inputDisplay: <CloudProvider id='azure' text='short' showIcon={true} />,
    dropdownDisplay: <CloudProvider id='azure' text='long' showIcon={true} />,
  },
]

const CloudProviderField: React.FunctionComponent<Props> = ({ value, onChange, isDisabled }) => {
  const { formatMessage } = useIntl()

  return (
    <EuiFormRow
      label={<FormattedMessage id='cloudProviderField.label' defaultMessage='Cloud Provider' />}
      aria-label={formatMessage({
        id: 'cloudProviderField.ariaLabel',
        defaultMessage: 'Select cloud provider',
      })}
    >
      <EuiSuperSelect
        options={OPTIONS}
        valueOfSelected={value}
        onChange={onChange}
        itemLayoutAlign='top'
        hasDividers={true}
        fullWidth={true}
        data-test-id='cloud-provider-select'
        disabled={isDisabled}
      />
    </EuiFormRow>
  )
}

export default CloudProviderField
